import { Button } from "@mixitone/components";
import { AkahuPoweredBy, AkahuSupportedBanks } from "@mixitone/components";
import { Account, type ClubAkahuConnection } from "@mixitone/models";
import { ApplicationView } from "@mixitone/mvc";
import {
  AKAHU_ABOUT,
  AKAHU_CLUB_LIMITED_BANKS,
  AKAHU_PAY_BY_BANK_BANKS,
  getAkahuClubSetupCopy,
  type AkahuSupportedBank,
} from "@mixitone/payments";
import { useEffect, useState } from "react";
import { actions } from "@mixitone/actions";
import { useMfaSessionStatus } from "lib/mfa/useMfaEnrollmentStatus";

interface ClubAkahuSetupBannerProps {
  connection: ClubAkahuConnection | null;
  loading?: boolean;
  connecting?: boolean;
  onConnect: () => void;
  /** When false, hide the supported-banks section (e.g. compact modal). Default true. */
  showSupportedBanks?: boolean;
}

const ClubAkahuSetupBanner: React.FC<ClubAkahuSetupBannerProps> = ({
  connection,
  loading = false,
  connecting = false,
  onConnect,
  showSupportedBanks = true,
}) => {
  const { ready: mfaReady } = useMfaSessionStatus();
  const hasEnduring = Account.current?.hasFeature("akahu_enduring") === true;
  const copy = getAkahuClubSetupCopy(hasEnduring);
  const [banks, setBanks] = useState<AkahuSupportedBank[]>(AKAHU_PAY_BY_BANK_BANKS);
  const [loadingBanks, setLoadingBanks] = useState(showSupportedBanks);

  useEffect(() => {
    if (!showSupportedBanks) {
      return;
    }

    let cancelled = false;
    setLoadingBanks(true);
    void actions.akahu
      .listSupportedBanks({})
      .then((response) => {
        if (!cancelled && response.banks?.length) {
          setBanks(response.banks);
        }
      })
      .catch(() => {
        // Keep curated fallback names.
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingBanks(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [showSupportedBanks]);

  if (loading) {
    return (
      <div className="rounded border border-gray-200 bg-gray-50 p-3 text-sm text-gray-600">
        {copy.checking}
      </div>
    );
  }

  if (connection?.isConnected) {
    const accountLabel =
      mfaReady && connection.payee_account_number
        ? connection.payee_account_number
        : connection.display_name_masked;
    return (
      <div className="rounded border border-green-300 bg-green-50 p-3 text-sm text-green-900 space-y-2">
        <div className="font-medium">{copy.headingReady}</div>
        {accountLabel && <div className="text-xs text-green-800">Account: {accountLabel}</div>}
        <p className="text-xs text-green-800">{copy.ready}</p>
        {showSupportedBanks && (
          <AkahuSupportedBanks
            banks={banks}
            loading={loadingBanks}
            compact
            description={AKAHU_CLUB_LIMITED_BANKS}
          />
        )}
      </div>
    );
  }

  const errorMessage =
    connection?.status === "error" ? connection.last_error_message || copy.previousFailed : null;

  return (
    <div className="rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950 space-y-3">
      <div>
        <div className="font-medium">{copy.title}</div>
        <p className="mt-1 text-xs text-amber-900">{copy.body}</p>
        <p className="mt-1 text-xs text-amber-900">{copy.redirect}</p>
      </div>

      {showSupportedBanks && (
        <AkahuSupportedBanks
          banks={banks}
          loading={loadingBanks}
          compact
          description={AKAHU_CLUB_LIMITED_BANKS}
        />
      )}

      {errorMessage && <p className="text-xs text-red-700">{errorMessage}</p>}

      <AkahuPoweredBy className="flex items-start gap-3 text-xs text-amber-900/80">
        {AKAHU_ABOUT}
      </AkahuPoweredBy>

      <div>
        <Button kind="secondary" onClick={onConnect} disabled={connecting}>
          {connecting ? "Redirecting…" : copy.cta}
        </Button>
      </div>
    </div>
  );
};

export default ApplicationView(ClubAkahuSetupBanner);
